import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { Action, User } from "@progress/kendo-angular-conversational-ui";
import { ChatBotService } from "./chat-bot.service";
import {
  delay,
  exhaustMap,
  filter,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import * as AuthenticationSelectors from "@app/shared/state/authentication/authentication.selectors";
import { Store } from "@ngrx/store";
import {
  ConnectUser,
  ChatBotMessage,
  ChatBotSendMessageRequest,
  ChatBotSendMessageResponse,
  ChatBotSendFeedbackRequest,
  ActionTypes,
  Prompt,
  FeedbackFormValue,
} from "./interfaces";
import { Observable } from "rxjs";
import { PhoenixToastService } from "@kehe/phoenix-notifications";
import { selectSuppliers } from "@app/shared/state/supplier/supplier.selectors";
import { questionsRequiringFollowUp } from "./prompts";
import { ISupplier } from "@app/modules/messages/models/ISupplier";
import { ResourceActions } from "@app/constants/resource-actions";

const generateMessageId = (): string => crypto.randomUUID();

export interface ChatBotState {
  isChatOpen: boolean;
  messages: ChatBotMessage[];
  prompts: Prompt[];
  associatedPrompt: string | undefined;
  isFeedbackFormOpen: boolean;
  activeSystemMessageId: string;
  feedbackFormValue: FeedbackFormValue;
}

export const BOT_USER: User = {
  id: 0,
};

const defaultState: ChatBotState = {
  isChatOpen: false,
  messages: [
    {
      id: generateMessageId(),
      author: BOT_USER,
      text: "Hi, I'm the Goodness Guru™. Ask me anything about supplier contracts, supplier onboarding, anything related to KeHE from our website, or select one of the below prompts.",
    },
  ],
  prompts: [],
  associatedPrompt: undefined,
  isFeedbackFormOpen: false,
  activeSystemMessageId: undefined,
  feedbackFormValue: undefined,
};

@Injectable()
export class ChatBotStore extends ComponentStore<ChatBotState> {
  constructor(
    private chatBotService: ChatBotService,
    private store: Store,
    private phoenixToastService: PhoenixToastService,
  ) {
    super(defaultState);
  }

  readonly messages$ = this.select((state) => state.messages);
  readonly prompts$ = this.select((state) => state.prompts);
  readonly associatedPrompt$ = this.select((state) => state.associatedPrompt);
  readonly isFeedbackFormOpen$ = this.select(
    (state) => state.isFeedbackFormOpen,
  );
  readonly activeSystemMessageId$ = this.select(
    (state) => state.activeSystemMessageId,
  );
  readonly feedbackFormValue$ = this.select((state) => state.feedbackFormValue);

  readonly selectUser$ = this.select(
    this.store.select(AuthenticationSelectors.selectUserEmail),
    (email) => {
      const user: User = {
        id: email,
      };
      return user;
    },
  );

  readonly selectViewModel$ = this.select(
    this.selectUser$,
    this.messages$,
    this.isFeedbackFormOpen$,
    (user, messages, isFeedbackFormOpen) => {
      return {
        user,
        messages,
        isFeedbackFormOpen,
      };
    },
  );

  readonly selectConnectUser$ = this.select(
    this.store.select(AuthenticationSelectors.selectUserFirstName),
    this.store.select(AuthenticationSelectors.selectUserLastName),
    this.store.select(AuthenticationSelectors.selectUserFullName),
    this.store.select(AuthenticationSelectors.selectUserEmail),
    this.store.select(AuthenticationSelectors.selectIsInternalUser),
    this.store.select(
      AuthenticationSelectors.selectDoesUserHaveAction(ResourceActions.Chatbot),
    ),
    this.store.select(selectSuppliers),
    (
      firstName,
      lastName,
      fullName,
      email,
      isInternalUser,
      hasChatBotResourceAction,
      suppliers,
    ) => {
      const connectUser: ConnectUser = {
        Name: fullName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        IsInternalUser: isInternalUser,
        IsConnectChatbotAuthorized: hasChatBotResourceAction,
        SupplierList: suppliers.map((supplier: ISupplier) => {
          return {
            name: supplier.name,
            esn: supplier.esn,
          };
        }),
      };
      return connectUser;
    },
  );

  readonly selectLatestUserMessage$ = this.select(
    this.messages$,
    this.selectUser$,
    (messages, user) =>
      messages?.filter((message) => message.author.id === user.id).pop(),
  );

  readonly selectSendMessageRequest$ = this.select(
    this.selectConnectUser$,
    this.selectLatestUserMessage$,
    (connectUser, message) => {
      const request: ChatBotSendMessageRequest = {
        RequestUser: connectUser,
        RequestMessage: message?.text,
      };
      return request;
    },
  );

  readonly selectSendFeedbackRequest$ = this.select(
    this.messages$,
    this.activeSystemMessageId$,
    this.feedbackFormValue$,
    (messages, activeSystemMessageId, feedbackFormValue) => {
      const message = messages.find(
        (message) => message.systemMessageId === activeSystemMessageId,
      );
      return <ChatBotSendFeedbackRequest>{
        conversationId: message.conversationId,
        systemMessageId: message.systemMessageId,
        userMessageId: message.userMessageId,
        requestMessage: message.text,
        responseFeedbackReason: feedbackFormValue?.reason,
        responseFeedbackInputText: feedbackFormValue?.comments,
      };
    },
  );

  readonly addUserMessage = this.updater((state, message: ChatBotMessage) => ({
    ...state,
    messages: [
      ...state.messages,
      message,
      {
        id: generateMessageId(),
        author: BOT_USER,
        typing: true,
        timestamp: new Date(),
      },
    ],
  }));

  getPrompts = this.effect((_) => {
    return _.pipe(
      exhaustMap(() =>
        this.chatBotService.getPrompts().pipe(
          tapResponse(
            (prompts) => this.patchState({ prompts }),
            (error) => console.error("Error loading prompts", error),
          ),
        ),
      ),
    );
  });

  addPromptsToInitialMessage = this.effect(() => {
    return this.prompts$.pipe(
      filter((prompts) => prompts?.length > 0),
      withLatestFrom(this.messages$),
      tap(([prompts, messages]) => {
        this.patchState(() => ({
          messages: messages.with(0, {
            ...messages[0],
            suggestedActions: this.mapPromptsToSuggestedActions(prompts),
          }),
        }));
      }),
    );
  });

  addPromptsToLatestResponse = this.effect((_) => {
    return _.pipe(
      withLatestFrom(this.prompts$, this.messages$),
      tap(([_, prompts, messages]) => {
        this.patchState(() => ({
          messages: messages.with(messages.length - 1, {
            ...messages[messages.length - 1],
            suggestedActions: this.mapPromptsToSuggestedActions(prompts),
          }),
        }));
      }),
    );
  });

  readonly sendMessage = this.effect(() => {
    return this.selectSendMessageRequest$.pipe(
      withLatestFrom(this.associatedPrompt$),
      filter(
        ([request]) =>
          !!request.RequestMessage &&
          !questionsRequiringFollowUp
            .map((q) => q.userQuestion)
            .includes(request.RequestMessage),
      ),
      switchMap(([request, associatedPrompt]) =>
        this.chatBotService
          .sendMessage({
            ...request,
            RequestMessagePrompt: associatedPrompt || "",
          })
          .pipe(
            tapResponse(
              (response: ChatBotSendMessageResponse) => {
                this.patchState((state: ChatBotState) => ({
                  associatedPrompt: undefined,
                  messages: [
                    ...state.messages,
                    {
                      id: generateMessageId(),
                      conversationId: response.conversationId,
                      systemMessageId: response.systemMessageId,
                      userMessageId: response.userMessageId,
                      author: BOT_USER,
                      text: response.Messages.ResponseMessage,
                      allowFeedback: true,
                      links: response.Messages.ResponseLinkObjects?.map(
                        (link) => {
                          return {
                            url: link.url,
                            title: link.title,
                            isExternal: link.isExternal,
                          };
                        },
                      ),
                    },
                    {
                      id: generateMessageId(),
                      author: BOT_USER,
                      text: "Is there anything else I can help you with?",
                      suggestedActions: [
                        { type: ActionTypes.MoreHelp, value: "Yes" },
                        { type: ActionTypes.NoMoreHelp, value: "No" },
                      ],
                    },
                  ],
                }));
              },
              () => {
                this.patchState((state: ChatBotState) => ({
                  messages: [
                    ...state.messages,
                    {
                      id: generateMessageId(),
                      author: BOT_USER,
                      text: "An error has occurred and I was unable to complete your request. Please try again.",
                    },
                  ],
                }));
              },
            ),
          ),
      ),
    );
  });

  readonly addUserMessageThatRequiresFollowUpQuestion = this.effect(() => {
    return this.selectSendMessageRequest$.pipe(
      filter(
        (request: ChatBotSendMessageRequest) =>
          !!request.RequestMessage &&
          questionsRequiringFollowUp
            .map((q) => q.userQuestion)
            .includes(request.RequestMessage),
      ),
      tap((request: ChatBotSendMessageRequest) => {
        this.patchState((state: ChatBotState) => ({
          messages: [
            ...state.messages,
            {
              id: generateMessageId(),
              author: BOT_USER,
              typing: true,
            },
          ],
          associatedPrompt: request.RequestMessage,
        }));
      }),
    );
  });

  readonly addFollowUpQuestion = this.effect(() => {
    return this.associatedPrompt$.pipe(
      delay(1000),
      withLatestFrom(this.selectSendMessageRequest$),
      filter(([associatedPrompt, request]) => !!associatedPrompt),
      tap(([associatedPrompt, request]) => {
        this.patchState((state: ChatBotState) => ({
          messages: [
            ...state.messages,
            {
              id: generateMessageId(),
              author: BOT_USER,
              text: questionsRequiringFollowUp.find(
                (q) => q.userQuestion === request.RequestMessage,
              )?.followUp,
              suggestedActions: [
                { type: ActionTypes.IDontKnow, value: `I don't know it` },
              ],
            },
          ],
        }));
      }),
    );
  });

  readonly processPrompt = this.effect((prompt$: Observable<Prompt>) => {
    return prompt$.pipe(
      withLatestFrom(this.messages$),
      tap(([prompt, messages]) => {
        this.patchState(() => ({
          messages: messages.with(messages?.length - 1, {
            ...messages[messages?.length - 1],
            suggestedActions: prompt.value.map((prompt) => {
              return <Action>{
                type: this.getSuggestedActionType(prompt),
                title: prompt.text,
                value: prompt.children.length ? prompt.children : prompt.text,
              };
            }),
          }),
        }));
      }),
    );
  });

  readonly addMoreHelpResponseFromChatBot = this.effect(
    (moreHelpNeeded$: Observable<boolean>) => {
      return moreHelpNeeded$.pipe(
        withLatestFrom(this.messages$),
        tap(([moreHelpNeeded, messages]) => {
          this.patchState((state) => ({
            messages: [
              ...state.messages,
              {
                id: generateMessageId(),
                author: BOT_USER,
                text: moreHelpNeeded
                  ? "OK, great! Ask me a question or use the prompts below."
                  : "Thank you for reaching out. If you have any more questions, feel free to ask. Have a great day!",
                suggestedActions: this.mapPromptsToSuggestedActions(
                  state.prompts,
                ),
              },
            ],
          }));
        }),
      );
    },
  );

  readonly userDoesNotKnowUpcOrEsn = this.effect((_) => {
    return _.pipe(
      withLatestFrom(this.messages$, this.selectUser$),
      tap(() => {
        this.patchState((state) => ({
          associatedPrompt: defaultState.associatedPrompt,
          messages: [
            ...state.messages,
            {
              id: generateMessageId(),
              author: BOT_USER,
              text: "Not a problem! Feel to free ask me another question or use the prompts below for more help.",
              suggestedActions: this.mapPromptsToSuggestedActions(
                state.prompts,
              ),
            },
          ],
        }));
      }),
    );
  });

  readonly sendFeedback = this.effect((isHelpful$: Observable<boolean>) => {
    return isHelpful$.pipe(
      withLatestFrom(this.selectSendFeedbackRequest$),
      exhaustMap(([isHelpful, request]) =>
        this.chatBotService
          .sendFeedback({ ...request, responseFeedback: isHelpful })
          .pipe(
            withLatestFrom(this.activeSystemMessageId$),
            tapResponse(
              ([response, activeSystemMessageId]) => {
                this.phoenixToastService.showSuccessToast(
                  "Thank you. Your feedback has been recorded.",
                );
                this.patchState((state) => ({
                  messages: [
                    ...state.messages.map((message) => {
                      if (message.systemMessageId === activeSystemMessageId) {
                        return {
                          ...message,
                          allowFeedback: false,
                        };
                      }
                      return message;
                    }),
                  ],
                  isFeedbackFormOpen: false,
                }));
              },
              () => {
                this.phoenixToastService.showErrorToast(
                  "An error occurred while recording your feedback. Please try again.",
                );
              },
            ),
          ),
      ),
    );
  });

  clearFeedbackFormOnCancel$ = this.effect(() => {
    return this.isFeedbackFormOpen$.pipe(
      filter((isFormOpen) => isFormOpen === false),
      tap(() => this.patchState({ feedbackFormValue: undefined })),
    );
  });

  private getSuggestedActionType(prompt: Prompt): ActionTypes {
    if (prompt.children.length) return ActionTypes.Prompt;
    return ActionTypes.Reply;
  }

  private mapPromptsToSuggestedActions(prompts: Prompt[]): Action[] {
    return prompts?.map((prompt) => {
      return <Action>{
        type: this.getSuggestedActionType(prompt),
        title: prompt.text,
        value: prompt.children.length ? prompt.children : prompt.text,
      };
    });
  }
}
